import React, { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import IHubDataset, {
    AutomationTypes,
} from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { isPanelScatterPlot } from '@utils-clean/panel-helpers';
import { UserContext } from '@contexts/user-context';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { Logger } from '@utils/logger';
import { ShowError, ShowMessage } from '@utils/notification';
import {
    GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED,
    GET_DATA_VIEW_CONFIGURATIONS,
    GET_PANELS,
    GET_STUDY_AUTOMATION_CONFIG,
} from '@queries/central-monitoring';
import {
    dataViewConfiguration_OpraQueries_dataViewConfiguration_DataViewConfiguration,
    dataViewConfigurations,
    dataViewConfigurationsVariables,
    getControlsHubConfigPanelsForStudyResolved,
    getControlsHubConfigPanelsForStudyResolvedVariables,
    getPanels,
    getPanels_OpraQueries_panels_Panel,
    getPanelsVariables,
    getStudyAutomationConfig,
    getStudyAutomationConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import AutomationApi from '@apis/bff-api/central-monitoring/automation';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import './generate-dialog.scss';

interface IProps {
    dataset: IHubDataset;
    studyRef: string;
    onClose: () => void;
    automationType: AutomationTypes;
}

const COMPONENT_NAME = 'generate-dialog';

const GenerateDialog = ({ dataset, studyRef, onClose, automationType }: IProps) => {
    const { t: tAdmin } = useTranslation('admin');
    const { t } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.automation.generateDialog',
    });
    const { t: tControls } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.controlsAutomation.generateDialog',
    });
    const { user } = useContext(UserContext);
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const [tab, setTab] = React.useState<'Sites' | 'Panels'>('Sites');

    const {
        data: config,
        loading: isLoadingConfig,
        error: configError,
    } = useQuery<getStudyAutomationConfig, getStudyAutomationConfigVariables>(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: studyRef },
    });
    const {
        data: allDatasets,
        isLoading: isLoadingAllDatasets,
        error: allDatasetsError,
    } = DatasetApi.useGetGrid(studyRef, automationType);

    const {
        data: allSites,
        isLoading: isLoadingAllSites,
        error: allSitesError,
    } = DatasetApi.useGetSites(studyRef, dataset.id, automationType === AutomationTypes.Controls);

    const {
        data: allDataViews,
        loading: isLoadingAllDataViews,
        error: allDataViewsError,
    } = useQuery<dataViewConfigurations, dataViewConfigurationsVariables>(GET_DATA_VIEW_CONFIGURATIONS, {
        skip: automationType === AutomationTypes.Controls,
        variables: {
            studyId: studyRef,
            timestamp: dataset.createdOn,
        },
    });

    const {
        data: allPanels,
        loading: isLoadingAllPanels,
        error: allPanelsError,
    } = useQuery<getPanels, getPanelsVariables>(GET_PANELS, {
        skip: !user || automationType === AutomationTypes.Controls,
        variables: {
            organisationId: user!.organisationId,
            studyId: studyRef,
        },
    });

    const {
        data: controlsHubPanels,
        loading: isLoadingControlsHubPanels,
        error: controlsHubPanelsError,
    } = useQuery<getControlsHubConfigPanelsForStudyResolved, getControlsHubConfigPanelsForStudyResolvedVariables>(
        GET_CONTROLS_HUB_CONFIG_PANELS_RESOLVED,
        {
            skip: !user || automationType === AutomationTypes.Site,
            variables: {
                studyId: studyRef,
                timestamp: dataset.createdOn,
            },
        },
    );

    const isLoading =
        isLoadingConfig ||
        isLoadingAllDatasets ||
        isLoadingAllSites ||
        isLoadingAllDataViews ||
        isLoadingAllPanels ||
        isLoadingControlsHubPanels;

    const sitesError = allDatasetsError || configError || allSitesError;
    const panelsError = allDatasetsError || allDataViewsError || allPanelsError || controlsHubPanelsError;

    const activePanels = useMemo(() => {
        const panels = allPanels?.panels?.filter(
            (panel): panel is getPanels_OpraQueries_panels_Panel => panel !== null,
        );

        const dataViews = allDataViews?.dataViewConfigurations?.filter(
            (dataView): dataView is dataViewConfiguration_OpraQueries_dataViewConfiguration_DataViewConfiguration =>
                dataView !== null,
        );

        if (!dataViews || !panels) {
            return undefined;
        }

        const activeDataViewPanelRefs = dataViews
            .filter((dataView) => dataView.isActive && dataView.mainPanelId)
            .map((dataView) => dataView.mainPanelId as string);

        return panels
            .filter((panel) => activeDataViewPanelRefs.includes(panel.id as string) && isPanelScatterPlot(panel))
            .map((panel) => ({
                ref: panel.id as string,
                title: panel.plotTitle ?? panel.name,
            }));
    }, [allDataViews, allPanels]);

    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [includedSiteItems, setIncludedSiteItems] = React.useState<DragDropListItemProps[]>([]);
    const [excludedSiteItems, setExcludedSiteItems] = React.useState<DragDropListItemProps[]>([]);

    const [includedPanelItems, setIncludedPanelItems] = React.useState<DragDropListItemProps[]>([]);
    const [excludedPanelItems, setExcludedPanelItems] = React.useState<DragDropListItemProps[]>([]);

    useEffect(() => {
        if (!allSites || !allDatasets || !config?.getStudyAutomationConfig || automationType !== AutomationTypes.Site)
            return;

        const mostRecentDataset = allDatasets.find((d) =>
            ['Successful', 'Failed'].includes(d.automation?.status ?? ''),
        );

        const included: DragDropListItemProps[] = [];
        const excluded: DragDropListItemProps[] = [];

        allSites.forEach((site) => {
            const isExcludedByStatus = config.getStudyAutomationConfig.excludedSiteStatuses.includes(site.status);
            const wasPreviouslyExcluded =
                mostRecentDataset?.automation?.excludedSites?.some((excludedSite) => excludedSite.id === site.id) ??
                false;

            const siteItem: DragDropListItemProps = {
                id: site.id,
                title: site.name,
                disabled: isExcludedByStatus,
            };

            if (isExcludedByStatus || wasPreviouslyExcluded) {
                excluded.push(siteItem);
            } else {
                included.push(siteItem);
            }
        });

        setIncludedSiteItems(included);
        setExcludedSiteItems(excluded);
    }, [allSites, config, allDatasets]);

    useEffect(() => {
        if (!activePanels || !allDatasets) return;

        const mostRecentDataset = allDatasets.find((d) =>
            ['Successful', 'Failed'].includes(d.automation?.status ?? ''),
        );

        const included: DragDropListItemProps[] = [];
        const excluded: DragDropListItemProps[] = [];

        activePanels
            .sort((a, b) => a.title!.localeCompare(b.title!))
            .forEach((panel) => {
                const wasPreviouslyExcluded =
                    mostRecentDataset?.automation?.excludedPanels?.some(
                        (excludedPanel) => excludedPanel.reference === panel.ref,
                    ) ?? false;

                const siteItem: DragDropListItemProps = {
                    id: panel.ref,
                    title: panel.title,
                };

                if (wasPreviouslyExcluded) {
                    excluded.push(siteItem);
                } else {
                    included.push(siteItem);
                }
            });

        setIncludedPanelItems(included);
        setExcludedPanelItems(excluded);
    }, [activePanels, allDatasets]);

    useEffect(() => {
        if (!controlsHubPanels || !allDatasets) return;

        const mostRecentDataset = allDatasets.find((d) =>
            ['Successful', 'Failed'].includes(d.automation?.status ?? ''),
        );

        const included: DragDropListItemProps[] = [];
        const excluded: DragDropListItemProps[] = [];

        controlsHubPanels.getControlsHubConfigPanelsResolvedForStudy?.panels
            ?.filter((p) => p?.active)
            .sort((a, b) => a!.plotTitle!.localeCompare(b!.plotTitle!))
            .forEach((panel) => {
                const wasPreviouslyExcluded =
                    mostRecentDataset?.automation?.excludedPanels?.some(
                        (excludedPanel) => excludedPanel.reference === panel?.panelRef,
                    ) ?? false;

                const panelItem: DragDropListItemProps = {
                    id: panel?.panelRef,
                    title: panel?.plotTitle,
                };

                if (wasPreviouslyExcluded) {
                    excluded.push(panelItem);
                } else {
                    included.push(panelItem);
                }
            });

        setIncludedPanelItems(included);
        setExcludedPanelItems(excluded);
    }, [controlsHubPanels, allDatasets]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const { trigger: add, isMutating: isAdding } = AutomationApi.useAdd(studyRef, dataset.id, automationType);
    const { trigger: run, isMutating: isStarting } = AutomationApi.useRun(studyRef, automationType);

    const getSubmitButtonLabel = () => {
        if (isAdding) return t('submitButtonLabels.adding');
        if (isStarting) return t('submitButtonLabels.starting');

        return t('submitButtonLabels.default');
    };

    const getIsSubmitButtonDisabled = () => {
        if (isLoading) return true;
        if (isAdding || isStarting) return true;

        return automationType === AutomationTypes.Site
            ? includedSiteItems.length === 0 || includedPanelItems.length === 0
            : includedPanelItems.length === 0;
    };

    const onSubmit = async () => {
        try {
            const automationId = await add({
                excludedSiteIds: excludedSiteItems.map((item) => item.id as number),
                excludedPanelRefs: excludedPanelItems.map((item) => item.id as string),
            });

            const isRunning = await run(automationId);

            if (!isRunning) {
                throw new Error('Call to run automation failed.');
            }

            ShowMessage({ text: t('notifications.success'), type: 'success' });

            await DatasetApi.invalidateGetGrid(studyRef, automationType);
            await DatasetApi.invalidateGetAutomation(studyRef, dataset.id, automationType);
            onClose();
        } catch (error) {
            Logger.error(error, 'GenerateDialog', 'onSubmit', 'Error adding and running automation');
            ShowError(t('notifications.error'));
        }
    };

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges('generate-automation-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    return (
        <DraggableDialog
            dataTestId="generate-dialog"
            className="generate-dialog"
            onClose={onCloseHandler}
            onSubmit={onSubmit}
            primaryBtnLabel={getSubmitButtonLabel()}
            primaryBtnDisabled={getIsSubmitButtonDisabled()}
            title={automationType === AutomationTypes.Site ? t('title') : tControls('title')}
            initialWidth={832}
            initialHeight={588}
        >
            <div className="top-fields">
                <dl>
                    <dt>Dataset</dt>
                    <dd>{stringDateWithoutTimeFromUtcNonNullable(dataset.date, 'DD-MMM-YYYY')}</dd>
                </dl>
                <hr />
                {configError ? (
                    <InfoField type="Error" message={t('errors.configLoad')} layout="center" />
                ) : automationType === AutomationTypes.Site ? (
                    <dl>
                        <dt>Automation Trigger</dt>
                        <dd>{tAdmin(`${config?.getStudyAutomationConfig.studyAutomationColour}`)}</dd>

                        <dt className="width-third">Site Statuses Excluded from Automation</dt>
                        <dd>
                            {config?.getStudyAutomationConfig.excludedSiteStatuses.length === 0
                                ? '-'
                                : config?.getStudyAutomationConfig.excludedSiteStatuses.join(', ')}
                        </dd>
                    </dl>
                ) : (
                    <dl>
                        <dt>Automation Trigger</dt>
                        <dd>{tAdmin(`${config?.getStudyAutomationConfig.controlsAutomationColor}`)}</dd>
                    </dl>
                )}
            </div>
            <Card
                data-testid="exclusion-list-card"
                className="exclusion-list-card"
                title={
                    automationType === AutomationTypes.Site ? (
                        <ChipList
                            data-testid="tabs"
                            value={tab}
                            data={[
                                { text: t('tabs.sites.title'), value: 'Sites' },
                                { text: t('tabs.panels.title'), value: 'Panels' },
                            ]}
                            onChange={(e) => {
                                setTab(e.value as 'Sites' | 'Panels');
                            }}
                        />
                    ) : (
                        t('tabs.panels.title')
                    )
                }
            >
                {tab === 'Sites' && automationType === AutomationTypes.Site ? (
                    sitesError ? (
                        <InfoField type="Error" message={t('tabs.sites.loadError')} layout="center" />
                    ) : (
                        <DragDropListsCore
                            className="site-exclusions"
                            list1Header={t('tabs.sites.leftListTitle')}
                            list2Header={t('tabs.sites.rightListTitle')}
                            list1Items={includedSiteItems}
                            list2Items={excludedSiteItems}
                            idField="id"
                            onChangeItemsLeft={(leftItems) => {
                                setIncludedSiteItems(leftItems);
                                setIsDirty(true);
                            }}
                            onChangeItemsRight={(rightItems) => {
                                setExcludedSiteItems(rightItems);
                                setIsDirty(true);
                            }}
                            isTwoLists
                            hideTransferAllButtons
                            disableReorder
                            disableFilterLeft={false}
                        />
                    )
                ) : panelsError ? (
                    <InfoField type="Error" message={t('tabs.panels.loadError')} layout="center" />
                ) : (
                    <DragDropListsCore
                        className="panel-exclusions"
                        list1Header={t('tabs.panels.leftListTitle')}
                        list2Header={t('tabs.panels.rightListTitle')}
                        list1Items={includedPanelItems}
                        list2Items={excludedPanelItems}
                        idField="id"
                        onChangeItemsLeft={(leftItems) => {
                            setIncludedPanelItems(leftItems);
                            setIsDirty(true);
                        }}
                        onChangeItemsRight={(rightItems) => {
                            setExcludedPanelItems(rightItems);
                            setIsDirty(true);
                        }}
                        isTwoLists
                        hideTransferAllButtons
                        disableReorder
                        disableFilterLeft={false}
                    />
                )}
            </Card>
            <div className={`loading-overlay ${!isLoading ? 'fade-out' : ''}`}>
                <Loading />
            </div>
        </DraggableDialog>
    );
};

export default GenerateDialog;
